/*
 * Die Rechnung hinter der Miniatur in der Palette: wie gross der Rahmen ist,
 * wie stark verkleinert wird und wo das Ergebnis darin sitzt.
 *
 * WARUM EINE EIGENE DATEI. Erstens ist das die einzige Stelle in der Palette,
 * die rechnet, und Rechnen laesst sich pruefen: `tests/widgetThumbnail.test.ts`
 * haelt sie fest. Zweitens muss diese Datei dafuer aus dem Node-Testlauf
 * ladbar bleiben, der TypeScript nur ausduennt; deshalb steht hier kein
 * einziger Import, kein JSX und nichts aus dem Browser.
 *
 * WARUM DIE MASSE HIER UND NICHT IM STYLESHEET. Der Massstab ist nur
 * auszurechnen, wenn beide Groessen bekannt sind, und eine CSS-Variable kann
 * JavaScript nicht lesen, ohne zu messen. Also stehen die Zahlen hier und
 * gehen als Variablen ans Stylesheet, nicht umgekehrt. Ein zweiter Satz
 * Zahlen im CSS waere ein Zwilling, von dem eines Tages nur einer geaendert
 * wird, und das Ergebnis waere eine Miniatur, die neben ihrem Rahmen sitzt.
 */

/** Rahmen einer gewoehnlichen Miniatur. */
export const THUMB_W = 112;
export const THUMB_H = 76;

/** Abstand zwischen zwei Kacheln, derselbe Wert wie `--space-2`. */
export const TILE_GAP = 8;

/** Rahmen einer queren Miniatur: zwei Spalten und der Abstand dazwischen. */
export const THUMB_W_WIDE = THUMB_W * 2 + TILE_GAP;

/**
 * Die Eigengroesse einer Rasterzelle am PC, in Pixeln.
 *
 * Dieselben Zahlen wie `GRID_SPECS.desktopResponsive` in `editorModel.ts`:
 * Zeilenhoehe 80, und zwoelf Spalten auf den rund 1200 px, die eine
 * Editor-Flaeche breit ist. Bewusst als eigene Konstanten und nicht als
 * Import: die Palette braucht ein Seitenverhaeltnis fuer ihre Miniatur, kein
 * Rastermass. Ein Import brachte eine Abhaengigkeit auf das Modell mit, und
 * er brachte Laufzeitcode in eine Datei, die der Testlauf ohne Bundler laedt.
 */
export const NATIVE_COL_PX = 100;
export const NATIVE_ROW_PX = 80;

/**
 * Ab dieser Standardbreite gilt ein Widget als quer.
 *
 * Eine quere Kachel sagt schon etwas ueber das Widget, bevor man die
 * Miniatur darin ansieht, und sie gibt dem Trend die doppelte Breite. Ohne
 * sie muesste ein acht Spalten breites Diagramm auf ein Siebtel herunter,
 * und uebrig bliebe ein Fleck.
 */
export const WIDE_FROM_COLSPAN = 6;

/** Nur die beiden Felder, auf die es hier ankommt. Der ganze Katalogeintrag
 *  waere zu viel verlangt fuer eine Rechnung ueber zwei Zahlen. */
export interface ThumbSize {
  colSpan: number;
  rowSpan: number;
}

export function isWide(entry: ThumbSize): boolean {
  return entry.colSpan >= WIDE_FROM_COLSPAN;
}

export interface ThumbGeometry {
  /** Eigengroesse des Widgets in Pixeln, so gross wird es wirklich
   *  gerendert. */
  nativeW: number;
  nativeH: number;
  /** Faktor fuer `transform: scale()`. */
  scale: number;
  /** Lage des verkleinerten Bildes im Rahmen. */
  left: number;
  top: number;
}

/**
 * Massstab und Lage der Miniatur.
 *
 * WARUM `transform: scale()` UND NICHT EIN KLEINER KASTEN. Ein Widget ist
 * fuer eine Rasterzelle gebaut, nicht fuer eine Briefmarke: die Stylesheets
 * der Renderer tragen feste Schriftgrade (`--fs-md` ist 13 px), eigene
 * Mindestbreiten (`.hv-trend` 320 px, `.hv-hc-tile` 220 px) und Abstaende in
 * Pixeln. In einen 112 px breiten Kasten gelegt passiert deshalb NICHT, was
 * man erwartet: die Schrift bliebe 13 px gross, die Mindestbreite gewaenne,
 * der Inhalt braeche um oder liefe ueber, und das Ergebnis saehe aus wie ein
 * kaputtes Widget statt wie ein kleines. Mit `scale()` rendert das Widget in
 * seiner Eigengroesse, und erst das fertige Bild wird verkleinert: alle
 * Verhaeltnisse bleiben, die Miniatur zeigt, was auf der Flaeche entstehen
 * wird.
 *
 * Der Massstab nimmt die kleinere der beiden Richtungen, das ganze Widget
 * ist also zu sehen und nicht ein Ausschnitt. Ein Rundinstrument (3x3) und
 * eine Verbundkachel (6x3) haben sehr verschiedene Eigengroessen, und genau
 * das soll man sehen: die Kachel wird kleiner dargestellt, weil sie groesser
 * ist.
 *
 * `left`/`top` zentrieren das verkleinerte Bild im Rahmen. Noetig, weil
 * `transform` den Platzbedarf im Layout NICHT aendert: das Element belegt
 * weiter seine Eigengroesse, und ohne diese Rechnung klebte die Miniatur an
 * der linken oberen Ecke.
 */
export function thumbGeometry(entry: ThumbSize): ThumbGeometry {
  const boxW = isWide(entry) ? THUMB_W_WIDE : THUMB_W;
  const boxH = THUMB_H;

  const nativeW = entry.colSpan * NATIVE_COL_PX;
  const nativeH = entry.rowSpan * NATIVE_ROW_PX;
  // Nie vergroessern: ein Widget, das in den Rahmen passt, soll in seiner
  // Groesse stehen. Hochskaliert saehe es weicher aus als auf der Flaeche und
  // versprache eine Genauigkeit, die es nicht hat.
  const scale = Math.min(1, boxW / nativeW, boxH / nativeH);

  return {
    nativeW,
    nativeH,
    scale,
    left: Math.round((boxW - nativeW * scale) / 2),
    top: Math.round((boxH - nativeH * scale) / 2),
  };
}
